import React from 'react'
import { css, cx } from '@emotion/css'
import {
  theme,
  ThemeColors
} from '@contrast-security-inc/design-system-foundations'
import { WithTooltipChildrenFnProps } from '../../design-system'

const cssBorderRadius = '4px'

export const thermometerCss = css`
  display: flex;
  justify-content: start;
  border-radius: ${cssBorderRadius};
  overflow: hidden;
  width: 100%;
  min-height: 8px;
`

export interface ThermometerValueRendererProps {
  /**
   * inline style that must be applied to the outer element returned by the
   * render prop
   */
  readonly width: string

  /**
   * The key that must be appled to the outer element returned by the render
   * prop
   */
  readonly key: number | string

  /**
   * The index of the value which is being rendered
   */
  readonly index?: number

  /**
   * The className which must be applied to the outer element returned by the
   * render prop
   */
  readonly className: string
}

interface OptionalTooltipProps {
  readonly getRef?: WithTooltipChildrenFnProps['ref']
  readonly onMouseEnter?: WithTooltipChildrenFnProps['onMouseEnter']
  readonly onMouseLeave?: WithTooltipChildrenFnProps['onMouseLeave']
}

export interface ThermometerProps extends OptionalTooltipProps {
  /**
   * The thermometer foreground fill value (e.g. 0.5)
   */
  readonly fillRatio?: number

  /**
   * The thermometer foreground fill color
   */
  readonly fillColor?: ThemeColors

  /**
   * The thermometer background fill color
   */
  readonly backgroundColor?: ThemeColors

  /**
   * Render callback for the value at a given index.
   */
  readonly renderValue?: (
    props: ThermometerValueRendererProps & OptionalTooltipProps
  ) => React.ReactElement<any>

  dataTestId?: string
  readonly e2eTestId?: string
}

function DefaultThermometerValue({
  className,
  getRef,
  onMouseEnter,
  onMouseLeave
}: OptionalTooltipProps & {
  readonly className: string
}) {
  return (
    <div
      className={className}
      ref={getRef}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    />
  )
}

export function Thermometer({
  fillRatio,
  fillColor = 'neutral50',
  backgroundColor = 'neutral30',
  getRef,
  onMouseEnter,
  onMouseLeave,
  renderValue,
  dataTestId,
  e2eTestId
}: ThermometerProps) {
  const fillWidth =
    !fillRatio && fillRatio !== 0 ? '100%' : fillRatio * 100 + '%'
  const cssBackground = cx(
    css`
      background: ${theme.color[backgroundColor]};
    `,
    thermometerCss
  )
  const cssFill = css`
    background-color: ${theme.color[fillColor]};
    border-radius: ${cssBorderRadius};
    width: ${fillWidth};
  `

  const props = {
    width: fillWidth,
    key: 1,
    index: 0,
    className: cssFill,
    getRef,
    onMouseEnter,
    onMouseLeave
  }

  return (
    <div
      className={cssBackground}
      data-testid={dataTestId}
      data-e2e={e2eTestId}
    >
      {renderValue ? (
        renderValue(props)
      ) : (
        <DefaultThermometerValue {...props} />
      )}
    </div>
  )
}
